// src/components/Quiz/QuizTypeFormation/styles.js
import styled, { keyframes, css } from 'styled-components';
import theme from '../../theme'; // Adjust path if needed
// import { IoIosArrowBack } from "react-icons/io"; // No longer needed

const ALL_CARDS_DISAPPEAR_DURATION_MS = 300; // Duration for all cards disappearing
const HEADER_ANIMATION_DURATION_MS = 300; // For header transitions (renamed from ANIMATION_DURATION_MS for clarity)

// Approximate dimensions for animation calculations
// These should ideally match the actual rendered sizes or be robust enough for slight variations.
const APPROX_BUTTON_WIDTH = 40; // e.g., 24px icon + 2 * 8px padding
const HEADER_GAP = 16; // e.g., theme.spacing.medium which is often 16px
const PROGRESS_BAR_SHIFT = APPROX_BUTTON_WIDTH + HEADER_GAP; // Total shift amount

// --- Keyframes ---
const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

// New animation for all cards disappearing simultaneously
const allCardsDisappearAnimation = keyframes`
  0% { opacity: 1; transform: scale(1); } /* Keep initial scale to avoid jump if card had other transforms */
  100% { opacity: 0; transform: scale(1); visibility: hidden; } /* Opacity to 0, maintain scale, then hide */
`;

// --- Base Styles ---
export const Container = styled.div`
  display: flex;
  flex-direction: column; /* Change container to column flex */
  justify-content: flex-start; /* Align content to top */
  align-items: center; /* Center content horizontally */
  min-height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  background-color: ${theme.colors.background};
  /* Remove padding here, apply to ContentWrapper if needed */
  overflow: hidden;
  position: relative;
  font-family: 'Gabarito', sans-serif;
  z-index: 1;
  margin-left: ${props => props.isMobile ? '0' : '320px'};
  width: ${props => props.isMobile ? '100vw' : 'calc(100vw - 320px)'};
`;

// --- Top Header ---
export const QuizTopHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 60px; /* Adjust height as needed */
  display: flex;
  align-items: center;
  padding: 0 ${theme.spacing.large};
  box-sizing: border-box;
  z-index: 100;
  gap: ${theme.spacing.medium};
  background-color: ${theme.colors.background}; /* Add background to prevent overlap issues */
  justify-content: flex-start; /* Pack items to the left */
  overflow: hidden; /* Prevent visual artifacts during animation */
  /* The ProgressBarWrapper will use flex-grow to take remaining space */
`;

export const ReturnButton = styled.button`
  /* Styles adapted from Onboarding/styles.js */
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${theme.spacing.small};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease, opacity ${HEADER_ANIMATION_DURATION_MS}ms ease-in-out;
  flex-shrink: 0; /* Prevent button from shrinking */

  /* No SVG specific styles */
  img {
    width: 24px; /* Match the size in index.js */
    height: 24px; /* Match the size in index.js */
    /* Add any filter/stroke if needed for the image, for now, direct image display */
  }

  &:hover:not(:disabled) {
    transform: scale(1.1); /* Slightly larger on hover */
    /* Adjust hover effect if needed for image */
  }

  &:disabled {
    /* Keep opacity 0.5 for disabled state, but $isHidden will override for first question */
    opacity: ${props => props.$isHidden ? 0 : 0.5};
    cursor: not-allowed;
    transform: none; /* Disable hover transform */
  }
  
  /* Control visibility via $isHidden prop */
  opacity: ${props => props.$isHidden ? 0 : 1};
  pointer-events: ${props => props.$isHidden ? 'none' : 'auto'};
`;

// Wrapper for the new ProgressBar component to control its flex behavior
export const ProgressBarWrapper = styled.div`
  flex-grow: 1;
  display: flex; 
  align-items: center; 
  position: relative; 
  top: 2px; 

  /* Conditional positioning and transform for centering and sliding */
  left: ${props => props.$isFirstQuestion ? '50%' : '0'}; /* Position left edge at screen center on first question */
  transform: ${props => props.$isFirstQuestion ? 'translateX(-50vw)' : 'translateX(0px)'}; /* Pull left by half viewport width on first question */
  transition: left ${HEADER_ANIMATION_DURATION_MS}ms ease-in-out, transform ${HEADER_ANIMATION_DURATION_MS}ms ease-in-out; /* Transition both properties */

  & > div { // Target the ProgressBarContainer component (root of ProgressBar)
    width: 100% !important; 
    margin-left: 0 !important; 
  }
`;

// --- Main Content Area (Below Header) ---
export const ContentWrapper = styled.div`
  /* No longer needs position relative if Container handles overall layout */
  width: 100%;
  max-width: 600px; /* Keep max width */
  flex-grow: 1; /* Takes up remaining vertical space in Container */
  display: flex;
  flex-direction: column;
  /* Remove height: 100% */
  padding: 0 ${theme.spacing.medium}; /* Add horizontal padding */
  padding-top: 70px; /* Space for the absolute header + a little margin */
  padding-bottom: ${theme.spacing.medium}; /* Space at the very bottom */
  gap: ${theme.spacing.medium}; /* Consistent gap between children */
  box-sizing: border-box;
  overflow: hidden; /* Prevent ContentWrapper itself from scrolling */
`;

// --- Logo and Bubble Row ---
export const LogoBubbleRow = styled.div`
  display: flex;
  align-items: center; /* Change from flex-end to center */
  width: 100%;
  gap: ${theme.spacing.medium};
  flex-shrink: 0;
  position: relative;
`;

export const LogoImage = styled.img`
  width: 30%;
  max-width: 80px;
  height: auto;
  object-fit: contain;
  flex-shrink: 0;
`;

export const ChatBubbleContainer = styled.div`
  position: absolute;
  left: calc(50% + 50px);
  transform: translateX(-50%);
  display: flex;
  z-index: 2;
  width: 70%;
  top: 50%; /* Add top positioning */
  transform: translate(-50%, -50%); /* Update transform to handle both X and Y centering */
`;

export const ChatText = styled.div`
  background-color: transparent;
  color: ${theme.colors.noir};
  padding: 0.8rem 1.2rem;
  border-radius: 1rem;
  font-family: Gabarito;
  font-size: ${theme.fontSizes.medium};
  text-align: left;
  position: relative;
  width: 100%; /* This will now fill the container width */
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border: 1px solid ${theme.colors.gris_clair_default};

  span {
    line-height: 1.4;
    vertical-align: middle;
  }

  .typing-cursor {
    opacity: 1;
    animation: ${blink} 1s step-end infinite;
    margin-left: 2px;
    display: inline-block;
    vertical-align: middle;
  }

  &:after {
    content: '';
    position: absolute;
    left: -10px;
    top: 50%;
    transform: translateY(-50%);
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid ${theme.colors.gris_clair_default};
  }

  &:before {
    content: '';
    position: absolute;
    left: -9px;
    top: 50%;
    transform: translateY(-50%);
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid transparent;
    z-index: 1;
  }
`;

const cardColors = [
  theme.colors.bleu_fonce, // blue
  theme.colors.orange, // orange
  theme.colors.vert_fonce, // green
  theme.colors.rouge // red
];

const cardShadowColors = [
  theme.colors.bleu_shadow, // blue shadow
  theme.colors.orange_shadow, // orange shadow
  theme.colors.vert_shadow, // green shadow
  theme.colors.rouge_shadow // red shadow
];

// --- Answer Area ---
export const AnswersContainer = styled.div`
  width: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  overflow-y: auto;
  padding-bottom: 10px; /* Add padding to show shadows */

`;

export const AnswerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.small};
  width: 100%;
  height: 100%;
  opacity: 1; /* Cards are now always visible, no fade based on typing */
  transition: none; /* No opacity transition needed here */
  pointer-events: ${props => props.$isDisabled ? 'none' : 'auto'}; /* Controlled by component logic */
`;

export const AnswerOptionCard = styled.button`
  background-color: ${props => cardColors[props.index % cardColors.length]};
  color: white;
  border: none;
  border-radius: 25px;
  padding: 10px;
  font-family: 'Gabarito', sans-serif;
  font-size: 100%;
  font-weight: ${theme.fontWeights.medium};
  text-align: center;
  cursor: pointer;
  /* Transition for the active state (press/release), box-shadow for default state */
  transition: transform 0.1s ease, box-shadow 0.1s ease, opacity 0.1s ease; /* Added opacity to transition */
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1.3;
  -webkit-tap-highlight-color: transparent;
  overflow: hidden;
  word-break: break-word;
  opacity: 1; /* Default opacity */
  box-shadow: ${props => props.active ? 'none' : `0px 3px 0px 0px ${cardShadowColors[props.index % cardShadowColors.length]}`};
  transform: ${props => props.active ? 'translateY(3px)' : 'translateY(0)'};

  &:hover:not(:disabled) {
    /* If active, stays pressed. If not active, transform does not change on hover. */
    transform: ${props => props.active ? 'translateY(3px)' : 'translateY(0)'};
    /* No box-shadow change on hover unless it's part of the active state change */
  }

  /* &:active:not(:disabled) is effectively handled by the 'active' prop now */

  &:disabled {
    cursor: not-allowed;
    /* If card is active (selected), keep opacity 1, otherwise fade it */
    opacity: ${props => props.active ? 1 : 0.6} !important;
    /* If card is active (selected), keep its pressed transform, otherwise no transform */
    transform: ${props => props.active ? 'translateY(3px)' : 'none'};
    /* Disabled cards (both active or not) get no shadow, consistent with active state and general disabled look */
    box-shadow: none;
  }

  /* Apply disappearing animation based on $areDisappearing prop */
  ${props => props.$areDisappearing && css`
    animation: ${allCardsDisappearAnimation} ${ALL_CARDS_DISAPPEAR_DURATION_MS}ms ease-in-out forwards;
    /* Important: disable transitions when animation is active to prevent conflict */
    transition: none;
    pointer-events: none; /* Also disable pointer events during animation */
  `}
`;

// --- JSP Button ---
export const JspButton = styled.button`
  border-radius: 15px;
  border: 1px solid ${theme.colors.gris_clair_default};
  background: #FFF;
  box-shadow: ${props => props.active ? 'none' : `0px 2px 0px 0px ${theme.colors.gris_clair_default}`};
  transform: ${props => props.active ? 'translateY(2px)' : 'translateY(0)'};
  width: 100%;
  height: 55px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 15px;
  margin: 0 0 90px 0;
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.1s ease;
  box-sizing: border-box;
  color: #4C4C4C;
  font-family: 'Gabarito', sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  -webkit-tap-highlight-color: transparent;
  outline: none;
  gap: 10px; /* Add gap between icon and text */

  img {
    width: 30px;
    height: 30px;
  }

  &:hover {
    transform: ${props => props.active ? 'translateY(3px)' : 'translateY(0)'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: ${props => props.active ? 1 : 0.6};
    transform: ${props => props.active ? 'translateY(2px)' : 'none'};
    box-shadow: none; // Active or not, disabled buttons get no shadow here.
                     // This is fine as active state also wants no shadow.
    background-color: ${props => props.active ? '#FFF' : theme.colors.background_light};
    // The border was '1px solid ${theme.colors.gris_clair_default}' by default.
    // We need to ensure the border property is fully specified here.
    border: 1px solid ${props => props.active ? theme.colors.gris_clair_default : theme.colors.lightGray};
  }
`;

// --- Loading & Messages (Unchanged) ---
export const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  opacity: ${props => props.isLoading ? 1 : 0};
  pointer-events: ${props => props.isLoading ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`;

export const QuizMessage = styled.div`
  font-size: ${theme.fontSizes.medium};
  color: ${props => props.isError ? theme.colors.secondary : theme.colors.noir};
  text-align: center;
  padding: ${theme.spacing.medium};
  margin-top: ${theme.spacing.large};
  background-color: ${theme.colors.background_light};
  border-radius: 10px;
  border: 1px solid ${props => props.isError ? theme.colors.secondary : theme.colors.gris_clair};
  max-width: 80%;
  margin-left: auto;
  margin-right: auto;
  flex-shrink: 0;
`;